/**
 * Latest Reports Section JavaScript
 * Uses Storefront API to fetch ALL products and sort by report_date metafield
 * Supports pagination for collections with 100+ products
 * Includes price prediction mini-chart with premium gating
 */

class LatestReports {
  constructor(sectionElement) {
    this.section = sectionElement;
    this.sectionId = this.section.getAttribute('data-section-id') || this.section.id.replace('latest-reports-', '');
    this.grid = this.section.querySelector('.latest-reports__grid');
    this.configScript = document.getElementById(`latest-reports-config-${this.sectionId}`);
    this.cards = [];
    this.observers = [];
    this.config = null;
    this.init();
  }

  async init() {
    if (!this.configScript || !this.grid) return;

    try {
      this.config = JSON.parse(this.configScript.textContent);
      await this.fetchAndRenderProducts();
    } catch (e) {
      console.error('LatestReports: Error initializing', e);
      this.showError('Error al cargar los informes');
    }
  }

  async fetchAndRenderProducts() {
    const { shopDomain, storefrontAccessToken } = window.StocksReportConfig || {};

    if (!storefrontAccessToken) {
      console.error('LatestReports: Storefront Access Token not configured');
      this.showError('Configuración de API pendiente. Contacta al administrador.');
      return;
    }

    try {
      // Fetch all products from the collection with pagination
      const products = await this.fetchAllProducts(shopDomain, storefrontAccessToken);

      if (products.length === 0) {
        this.showEmpty();
        return;
      }

      // Sort by report_date (newest first)
      let sortedProducts = this.sortByReportDate(products);

      // Handle display mode
      if (this.config.settings.displayMode === 'random') {
        sortedProducts = this.shuffleArray(sortedProducts);
      }

      // Store all available products for load-more
      this.allProducts = sortedProducts;
      this.visibleCount = this.config.settings.showCount;
      console.log(`[LatestReports] Total products fetched: ${sortedProducts.length}, initial show: ${this.visibleCount}`);

      // Render initial cards
      const productsToShow = sortedProducts.slice(0, this.visibleCount);
      this.renderCards(productsToShow);
      this.updateLoadMoreButton();
      this.setupLoadMoreButton();

    } catch (e) {
      this._retryCount = (this._retryCount || 0) + 1;
      if (this._retryCount < 5) {
        console.warn(`LatestReports: Error fetching products, retry ${this._retryCount}/5 in 1.5s...`, e);
        await new Promise(r => setTimeout(r, 1500));
        return this.fetchAndRenderProducts();
      }
      console.error('LatestReports: All 5 retries failed', e);
      this.showError('Error al cargar los informes. Inténtalo de nuevo.');
    }
  }

  async fetchAllProducts(shopDomain, token) {
    const allProducts = [];
    let hasNextPage = true;
    let cursor = null;

    while (hasNextPage) {
      const result = await this.fetchProductsPage(shopDomain, token, cursor);
      allProducts.push(...result.products);
      hasNextPage = result.hasNextPage;
      cursor = result.endCursor;
    }

    return allProducts;
  }

  async fetchProductsPage(shopDomain, token, cursor = null) {
    const afterClause = cursor ? `, after: "${cursor}"` : '';

    const query = `
      query GetCollectionProducts($handle: String!) {
        collection(handle: $handle) {
          products(first: 250${afterClause}) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                title
                handle
                onlineStoreUrl
                createdAt
                report_date: metafield(namespace: "custom", key: "report_date") {
                  value
                }
                company_name: metafield(namespace: "custom", key: "company_name") {
                  value
                }
                ticker_symbol: metafield(namespace: "custom", key: "ticker_symbol") {
                  value
                }
                seo {
                  description
                }
                summary_description: metafield(namespace: "custom", key: "summary_description") {
                  value
                }
                overall_score: metafield(namespace: "custom", key: "overall_score") {
                  value
                }
                fundamentos_score: metafield(namespace: "custom", key: "fundamentos_score") {
                  value
                }
                crecimiento_score: metafield(namespace: "custom", key: "crecimiento_score") {
                  value
                }
                rentabilidad_score: metafield(namespace: "custom", key: "rentabilidad_score") {
                  value
                }
                eficiencia_score: metafield(namespace: "custom", key: "eficiencia_score") {
                  value
                }
                momentum_score: metafield(namespace: "custom", key: "momentum_score") {
                  value
                }
                valoracion_score: metafield(namespace: "custom", key: "valoracion_score") {
                  value
                }
                tesis_validation: metafield(namespace: "custom", key: "tesis_validation") {
                  value
                }
                validated_tesis: metafield(namespace: "custom", key: "validated_tesis") {
                  value
                }
                precio_actual: metafield(namespace: "custom", key: "precio_actual") {
                  value
                }
                precio_base_6m: metafield(namespace: "custom", key: "precio_base_6m") {
                  value
                }
                precio_base_12m: metafield(namespace: "custom", key: "precio_base_12m") {
                  value
                }
                precio_base_24m: metafield(namespace: "custom", key: "precio_base_24m") {
                  value
                }
                precio_pesimista_6m: metafield(namespace: "custom", key: "precio_pesimista_6m") {
                  value
                }
                precio_pesimista_12m: metafield(namespace: "custom", key: "precio_pesimista_12m") {
                  value
                }
                precio_pesimista_24m: metafield(namespace: "custom", key: "precio_pesimista_24m") {
                  value
                }
                precio_optimista_6m: metafield(namespace: "custom", key: "precio_optimista_6m") {
                  value
                }
                precio_optimista_12m: metafield(namespace: "custom", key: "precio_optimista_12m") {
                  value
                }
                precio_optimista_24m: metafield(namespace: "custom", key: "precio_optimista_24m") {
                  value
                }
                price_evolutiom: metafield(namespace: "custom", key: "price_evolutiom") {
                  references(first: 50) {
                    edges {
                      node {
                        ... on Metaobject {
                          fields {
                            key
                            value
                          }
                        }
                      }
                    }
                  }
                }
                analyst_targets: metafield(namespace: "custom", key: "analyst_targets") {
                  references(first: 10) {
                    edges {
                      node {
                        ... on Metaobject {
                          fields {
                            key
                            value
                          }
                        }
                      }
                    }
                  }
                }
                scorecard_detail_free: metafield(namespace: "custom", key: "scorecard_detail_free") {
                  value
                }
                collections(first: 5) {
                  edges {
                    node {
                      handle
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetch(`https://${shopDomain}/api/2024-01/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({
        query,
        variables: { handle: this.config.collectionHandle }
      })
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    const data = await response.json();

    if (data.errors) {
      console.error('GraphQL errors:', data.errors);
      throw new Error('GraphQL query failed');
    }

    const collection = data.data?.collection;
    if (!collection) {
      return { products: [], hasNextPage: false, endCursor: null };
    }

    const products = collection.products.edges.map(edge => this.transformProduct(edge.node));

    return {
      products,
      hasNextPage: collection.products.pageInfo.hasNextPage,
      endCursor: collection.products.pageInfo.endCursor
    };
  }

  transformProduct(node) {
    return {
      id: node.id,
      title: node.title,
      handle: node.handle,
      url: node.onlineStoreUrl || `/products/${node.handle}`,
      createdAt: node.createdAt,
      report_date: node.report_date?.value || null,
      company_name: node.company_name?.value || node.title,
      ticker: node.ticker_symbol?.value || node.handle.toUpperCase(),
      summary: node.summary_description?.value || '',
      overall_score: parseInt(node.overall_score?.value) || 0,
      fundamentos_score: parseInt(node.fundamentos_score?.value) || 0,
      crecimiento_score: parseInt(node.crecimiento_score?.value) || 0,
      rentabilidad_score: parseInt(node.rentabilidad_score?.value) || 0,
      eficiencia_score: parseInt(node.eficiencia_score?.value) || 0,
      momentum_score: parseInt(node.momentum_score?.value) || 0,
      valoracion_score: parseInt(node.valoracion_score?.value) || 0,
      tesis_validation: node.tesis_validation?.value || null,
      validated_tesis: node.validated_tesis?.value || null,
      precio_actual: node.precio_actual?.value || null,
      precio_base_6m: node.precio_base_6m?.value || null,
      precio_base_12m: node.precio_base_12m?.value || null,
      precio_base_24m: node.precio_base_24m?.value || null,
      precio_pesimista_6m: node.precio_pesimista_6m?.value || null,
      precio_pesimista_12m: node.precio_pesimista_12m?.value || null,
      precio_pesimista_24m: node.precio_pesimista_24m?.value || null,
      precio_optimista_6m: node.precio_optimista_6m?.value || null,
      precio_optimista_12m: node.precio_optimista_12m?.value || null,
      precio_optimista_24m: node.precio_optimista_24m?.value || null,
      price_evolution: this.transformPriceEvolution(node.price_evolutiom),
      analyst_targets: this.transformAnalystTargets(node.analyst_targets),
      isFreeReport: this.isProductFree(node)
    };
  }

  isProductFree(node) {
    // If scorecard_detail_free has no content, treat as free
    if (!node.scorecard_detail_free?.value) return true;
    // If product belongs to informes-gratuitos collection
    const collections = node.collections?.edges || [];
    return collections.some(edge => edge.node.handle === 'informes-gratuitos');
  }

  transformPriceEvolution(metafield) {
    if (!metafield?.references?.edges) return [];
    return metafield.references.edges.map(edge => {
      const fields = edge.node?.fields || [];
      const obj = {};
      fields.forEach(f => { obj[f.key] = f.value; });
      return { date: obj.date || '', price: obj.price || '', is_promising: obj.is_promising === 'true', is_failing: obj.is_failing === 'true', is_promising_stop: obj.is_promising_stop === 'true' };
    }).filter(sp => sp.date && sp.price);
  }

  transformAnalystTargets(metafield) {
    if (!metafield?.references?.edges) return [];
    return metafield.references.edges.map(edge => {
      const fields = edge.node?.fields || [];
      const obj = {};
      fields.forEach(f => { obj[f.key] = f.value; });
      return obj;
    });
  }

  // === Price parsing and chart rendering ===

  parsePrice(raw) {
    if (!raw) return { value: null, currency: '' };

    let currency = '';
    if (raw.includes('HK$')) currency = 'HK$';
    else if (raw.includes('HKD')) currency = 'HKD';
    else if (raw.includes('R$')) currency = 'R$';
    else if (raw.includes('SEK')) currency = 'SEK';
    else if (raw.includes('NOK')) currency = 'NOK';
    else if (raw.includes('DKK')) currency = 'DKK';
    else if (raw.includes('CHF')) currency = 'CHF';
    else if (raw.includes('INR')) currency = 'INR';
    else if (raw.includes('KRW')) currency = 'KRW';
    else if (raw.includes('TWD')) currency = 'TWD';
    else if (raw.includes('JPY')) currency = 'JPY';
    else if (raw.includes('CNY')) currency = 'CNY';
    else if (raw.includes('SGD')) currency = 'SGD';
    else if (raw.includes('AUD')) currency = 'AUD';
    else if (raw.includes('CAD')) currency = 'CAD';
    else if (raw.includes('kr')) currency = 'kr';
    else if (raw.includes('Rs')) currency = 'Rs';
    else if (raw.includes('₹')) currency = '₹';
    else if (raw.includes('€') || raw.includes('&#8364;')) currency = '€';
    else if (raw.includes('£')) currency = '£';
    else if (raw.includes('₩')) currency = '₩';
    else if (raw.includes('¥')) currency = '¥';
    else if (raw.includes('PLN')) currency = 'PLN';
    else if (raw.includes('zł')) currency = 'zł';
    else if (raw.includes('$')) currency = '$';

    const cleaned = raw
      .replace(/HK\$|HKD/g, '')
      .replace(/R\$/g, '')
      .replace(/SEK|NOK|DKK|CHF|INR|KRW|TWD|JPY|CNY|SGD|AUD|CAD/g, '')
      .replace(/&#8364;/g, '')
      .replace(/[€$£₹₩¥]/g, '')
      .replace(/zł|PLN/g, '')
      .replace(/kr/g, '')
      .replace(/Rs/g, '')
      .replace(/\s/g, '')
      .replace(/,/g, '');

    const value = parseFloat(cleaned);
    return { value: isNaN(value) ? null : value, currency };
  }

  computePriceData(product) {
    const actual = this.parsePrice(product.precio_actual);
    const p6m = this.parsePrice(product.precio_base_6m);
    const p12m = this.parsePrice(product.precio_base_12m);
    const p24m = this.parsePrice(product.precio_base_24m);

    const currency = actual.currency || p6m.currency || p12m.currency || p24m.currency;

    const points = [
      { label: 'Actual', value: actual.value },
      { label: '6M', value: p6m.value },
      { label: '12M', value: p12m.value },
      { label: '24M', value: p24m.value }
    ];

    // Need at least actual + one target
    const validPoints = points.filter(p => p.value !== null);
    if (validPoints.length < 2 || actual.value === null) return null;

    // Compute variations relative to actual price
    for (let i = 1; i < points.length; i++) {
      if (points[i].value !== null && actual.value > 0) {
        points[i].variation = ((points[i].value - actual.value) / actual.value) * 100;
      }
    }

    return { points, currency };
  }

  formatPrice(value, currency) {
    if (value >= 1000) {
      return currency + value.toFixed(0);
    }
    return currency + (value % 1 === 0 ? value.toFixed(0) : value.toFixed(2));
  }

  renderPriceChart(product) {
    const data = this.computePriceData(product);
    if (!data) return '';

    const { currency } = data;
    const canvasId = 'chart-' + product.handle;

    /* Store chart data for rendering after DOM insert */
    if (!this._pendingCharts) this._pendingCharts = [];
    this._pendingCharts.push({
      canvasId,
      opts: {
        currency,
        actual: data.points[0].value,
        reportDate: product.report_date || '',
        base: {
          m6: data.points[1]?.value || 0,
          m12: data.points[2]?.value || 0,
          m24: data.points[3]?.value || 0
        },
        bear: {
          m6: this.parsePrice(product.precio_pesimista_6m).value || 0,
          m12: this.parsePrice(product.precio_pesimista_12m).value || 0,
          m24: this.parsePrice(product.precio_pesimista_24m).value || 0
        },
        bull: {
          m6: this.parsePrice(product.precio_optimista_6m).value || 0,
          m12: this.parsePrice(product.precio_optimista_12m).value || 0,
          m24: this.parsePrice(product.precio_optimista_24m).value || 0
        },
        evolution: product.price_evolution || [],
        analystTargets: product.analyst_targets || [],
        validatedTesis: product.validated_tesis || 'Base',
        showLegend: false,
        compact: true
      }
    });

    return `<div class="price-chart__canvas-wrap"><canvas id="${canvasId}"></canvas></div>`;
  }

  /* Render all pending Chart.js charts after cards are in the DOM */
  renderPendingCharts() {
    if (!this._pendingCharts || !window.InformeChart) return;
    this._pendingCharts.forEach(item => {
      const canvas = document.getElementById(item.canvasId);
      if (!canvas) return;
      /* Destroy existing chart on this canvas if any */
      const existing = Chart.getChart(canvas);
      if (existing) existing.destroy();
      window.InformeChart.render(canvas, item.opts);
    });
    this._pendingCharts = [];
  }

  // === End price chart ===

  truncateText(text, maxLength) {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  }

  sortByReportDate(products) {
    return [...products].sort((a, b) => {
      // Use report_date if available, otherwise fall back to createdAt
      const dateA = new Date(a.report_date || a.createdAt || '1970-01-01');
      const dateB = new Date(b.report_date || b.createdAt || '1970-01-01');
      return dateB - dateA; // Descending order (newest first)
    });
  }

  shuffleArray(array) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  renderCards(products) {
    /* Destroy existing Chart.js instances before clearing DOM */
    this.grid.querySelectorAll('canvas').forEach(c => {
      const ch = Chart.getChart(c);
      if (ch) ch.destroy();
    });
    this.grid.innerHTML = '';

    products.forEach(product => {
      const card = this.createCardElement(product);
      this.grid.appendChild(card);
    });

    this.cards = this.section.querySelectorAll('.latest-reports__card');
    this.setupCardObservers();
    this.setupPremiumTooltips();
    this.renderPendingCharts();
  }

  setupLoadMoreButton() {
    const loadMoreBtn = this.section.querySelector('.latest-reports__load-more-btn');
    if (!loadMoreBtn) return;
    loadMoreBtn.addEventListener('click', () => this.loadMore());
  }

  updateLoadMoreButton() {
    const settings = this.config.settings;
    const loadMoreBtn = this.section.querySelector('.latest-reports__load-more-btn');
    if (!loadMoreBtn) return;

    const remaining = this.allProducts.length - this.visibleCount;
    if (!settings.enableLoadMore || remaining <= 0) {
      loadMoreBtn.style.display = 'none';
    } else {
      loadMoreBtn.style.display = '';
      const nextBatch = Math.min(settings.loadMoreBatch || 6, remaining);
      loadMoreBtn.textContent = `${settings.loadMoreLabel || 'Cargar más tesis'} (${remaining})`;
    }
  }

  loadMore() {
    const batchSize = this.config.settings.loadMoreBatch || 6;
    const nextCount = Math.min(this.visibleCount + batchSize, this.allProducts.length);
    const newProducts = this.allProducts.slice(this.visibleCount, nextCount);

    newProducts.forEach(product => {
      const card = this.createCardElement(product);
      this.grid.appendChild(card);
    });

    this.visibleCount = nextCount;
    this.cards = this.section.querySelectorAll('.latest-reports__card');
    this.setupCardObservers();
    this.setupPremiumTooltips();
    this.renderPendingCharts();
    this.updateLoadMoreButton();
  }

  createCardElement(product) {
    const article = document.createElement('article');
    article.className = 'latest-reports__card';
    article.setAttribute('data-card-id', product.id);
    article.setAttribute('data-report-date', product.report_date || '');

    const metrics = [
      { key: 'fundamentos_score', label: 'Fund.' },
      { key: 'crecimiento_score', label: 'Crec.' },
      { key: 'rentabilidad_score', label: 'Rent.' },
      { key: 'eficiencia_score', label: 'Efic.' },
      { key: 'momentum_score', label: 'Mom.' },
      { key: 'valoracion_score', label: 'Val.' }
    ];

    const categoriesHTML = metrics.map(m => {
      const score = product[m.key] || 0;
      return `
        <div class='latest-reports__category-row'>
          <div class='latest-reports__category-header'>
            <span class='latest-reports__category-label'>${m.label}</span>
            <span class='latest-reports__category-value'>${score}</span>
          </div>
          <div class='latest-reports__progress-bar'>
            <div class='latest-reports__progress-fill' style='width: ${score}%'></div>
          </div>
        </div>
      `;
    }).join('');

    const settings = this.config.settings;
    const buttonArrowClass = settings.buttonArrow ? 'button--arrow' : '';
    const summaryHTML = product.summary ? `<p class='latest-reports__summary'>${this.escapeHTML(product.summary)}</p>` : '';

    // Price chart
    const chartSVG = this.renderPriceChart(product);
    let priceChartHTML = '';

    if (chartSVG) {
      {
        priceChartHTML = `
          <div class="latest-reports__price-chart">
            <span class="latest-reports__price-chart-title">Escenario base</span>
            ${chartSVG}
          </div>
        `;
      }
    }

    const var6mBadge = this.getVar6mBadge(product);

    article.innerHTML = `
      <div class='latest-reports__content'>
        <div class='latest-reports__header'>
          <div class='latest-reports__header-left'>
            <span class='latest-reports__ticker'>${this.escapeHTML(product.ticker)}</span>
            <h3 class='latest-reports__title'>
              <a href='${product.url}'>${this.escapeHTML(product.company_name)}</a>
            </h3>
          </div>
          <div class='latest-reports__badges'>${var6mBadge}</div>
        </div>

        ${summaryHTML}

        ${priceChartHTML}

        <div class='latest-reports__scorecard'>
          <div class='latest-reports__overall'>
            <div class='latest-reports__overall-score'>
              <span class='latest-reports__overall-value'>${product.overall_score}</span>
              <span class='latest-reports__overall-max'>/100</span>
            </div>
            <span class='latest-reports__overall-label'>Overall Score</span>
          </div>

          <div class='latest-reports__categories'>
            ${categoriesHTML}
          </div>
        </div>

        ${this.renderAnalysisBadges(product)}

        <div class='latest-reports__cta'>
          <a href='${product.url}' class='button button--${settings.buttonStyle} button--small ${buttonArrowClass}'>
            ${this.escapeHTML(settings.cardButtonLabel)}
          </a>
        </div>
      </div>
    `;

    return article;
  }

  getVar6mBadge(product) {
    const actual = this.parsePrice(product.precio_actual).value || 0;
    const b6 = this.parsePrice(product.precio_base_6m).value || 0;
    const bu6 = this.parsePrice(product.precio_optimista_6m).value || 0;
    const br6 = this.parsePrice(product.precio_pesimista_6m).value || 0;
    if (!(actual > 0 && b6 > 0)) return '';

    const vt = (product.validated_tesis || 'Base').trim();
    let obj6 = b6;
    if (vt === 'Optimista' && bu6 > 0) obj6 = bu6;
    else if (vt === 'Pesimista' && br6 > 0) obj6 = br6;

    let realPrice = actual;
    const evo = product.price_evolution || [];
    if (evo.length > 0) {
      const lp = this.parsePrice(evo[evo.length - 1].price).value;
      if (lp > 0) realPrice = lp;
    }

    const var6 = (obj6 - realPrice) / realPrice * 100;
    const sign = var6 > 0 ? '+' : '';
    const color = var6 >= 0 ? '#16a34a' : '#dc2626';
    const bg = var6 >= 0 ? '#f0fdf4' : '#fef2f2';
    return `<span class='tesis-badge' style='color:${color};background:${bg}'>Obj. 6M ${sign}${var6.toFixed(1)}%</span>`;
  }

  getSRvsConsensusBadge(product) {
    const actual = this.parsePrice(product.precio_actual).value || 0;
    const b6 = this.parsePrice(product.precio_base_6m).value || 0;
    const bu6 = this.parsePrice(product.precio_optimista_6m).value || 0;
    const br6 = this.parsePrice(product.precio_pesimista_6m).value || 0;
    const atData = product.analyst_targets || [];
    const evoData = product.price_evolution || [];
    if (!(actual > 0 && atData.length > 0 && evoData.length > 0 && b6 > 0)) return null;

    const rdStr = product.report_date || '';
    let reportDate = rdStr ? new Date(rdStr.split('-').length === 2 ? rdStr + '-01' : rdStr) : null;
    if (!reportDate || isNaN(reportDate.getTime())) return null;

    const vt = (product.validated_tesis || 'Base').toLowerCase();
    let sr6 = b6;
    if (vt === 'optimista' || vt === 'bull') sr6 = bu6;
    else if (vt === 'pesimista' || vt === 'bear') sr6 = br6;

    let lastReal = null;
    evoData.forEach(sp => {
      const d = new Date(sp.date);
      if (isNaN(d.getTime())) return;
      const p = parseFloat(String(sp.price || '').replace(/[^0-9.,\-]/g, '').replace(/,/g, '')) || 0;
      if (p > 0 && (!lastReal || d.getTime() > lastReal.time)) lastReal = { time: d.getTime(), price: p };
    });
    if (!lastReal || sr6 <= 0) return null;

    const months = (lastReal.time - reportDate.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
    if (months < 1) return null;

    const lerp = (a, b, t) => a + (b - a) * Math.max(0, Math.min(1, t));
    const srT = months <= 6 ? lerp(actual, sr6, months / 6) : sr6;

    let latest = null;
    atData.forEach(raw => {
      const d = new Date(raw.date);
      if (isNaN(d.getTime())) return;
      const mean = parseFloat(String(raw.target_mean || '').replace(/[^0-9.,\-]/g, '').replace(/,/g, '')) || 0;
      if (mean > 0 && (!latest || d.getTime() > latest.ts)) latest = { ts: d.getTime(), mean };
    });
    if (!latest) return null;

    const conT = months <= 12 ? lerp(actual, latest.mean, months / 12) : latest.mean;
    const srErr = Math.abs((srT - lastReal.price) / lastReal.price * 100);
    const conErr = Math.abs((conT - lastReal.price) / lastReal.price * 100);
    const diff = Math.abs(srErr - conErr);

    let wLabel, wColor, wBg;
    if (diff < 2) { wLabel = 'Tesis y consenso coinciden'; wColor = '#3b82f6'; wBg = '#eff6ff'; }
    else if (srErr < conErr) { wLabel = 'Tesis confirma vs consenso'; wColor = '#16a34a'; wBg = '#f0fdf4'; }
    else { wLabel = 'Consenso confirma'; wColor = '#e67e22'; wBg = '#fff7ed'; }
    return { label: wLabel, color: wColor, bg: wBg };
  }

  renderAnalysisBadges(product) {
    const isPremium = this.config.isPremium;
    const isFree = product.isFreeReport;
    const lockSvg = `<svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' style='vertical-align:-1px;margin-right:2px'><rect x='3' y='11' width='18' height='11' rx='2'/><path d='M7 11V7a5 5 0 0 1 10 0v4'/></svg>`;

    /* Tesis cumplimiento badge */
    const tv = (product.tesis_validation || '').trim();
    const vt = (product.validated_tesis || '').trim();
    let teClass = 'pending', teLabel = 'Pendiente';
    if (tv === 'Débil' && vt === 'Optimista') { teClass = 'excedida'; teLabel = 'Tesis excedida'; }
    else if (tv === 'Fuerte' && vt === 'Optimista') { teClass = 'fuerte'; teLabel = 'Tesis fuerte'; }
    else if (tv === 'Fuerte' && vt === 'Base') { teClass = 'confirmada'; teLabel = 'Tesis confirmada'; }
    else if (tv === 'Débil' && vt === 'Pesimista') { teClass = 'rota'; teLabel = 'Tesis revertida'; }
    else if (tv === 'Débil' || vt === 'Pesimista') { teClass = 'debil'; teLabel = 'Tesis débil'; }

    /* SR vs Consenso — always visible regardless of plan */
    let badges = [];
    const srVsCon = this.getSRvsConsensusBadge(product);
    if (srVsCon) {
      badges.push(`<span class='tesis-badge' style='color:${srVsCon.color};background:${srVsCon.bg}'><span class='tesis-badge__dot' style='background:${srVsCon.color}'></span>${srVsCon.label}</span>`);
    }

    if (!isPremium && !isFree) {
      badges.push(`<span class='tesis-badge' style='color:#6b7280;background:#f3f4f6'>${lockSvg}Cumplimiento</span>`);
      badges.push(`<span class='tesis-badge' style='color:#6b7280;background:#f3f4f6'>${lockSvg}Upside</span>`);
      badges.push(`<span class='tesis-badge' style='color:#6b7280;background:#f3f4f6'>${lockSvg}Asimetría</span>`);
      return `<div class='latest-reports__analysis-badges'>${badges.join('')}</div>`;
    }

    const actual = this.parsePrice(product.precio_actual).value || 0;
    const b6 = this.parsePrice(product.precio_base_6m).value || 0;
    const b12 = this.parsePrice(product.precio_base_12m).value || 0;
    const b24 = this.parsePrice(product.precio_base_24m).value || 0;
    const br6 = this.parsePrice(product.precio_pesimista_6m).value || 0;
    const br12 = this.parsePrice(product.precio_pesimista_12m).value || 0;
    const br24 = this.parsePrice(product.precio_pesimista_24m).value || 0;
    const bu6 = this.parsePrice(product.precio_optimista_6m).value || 0;
    const bu12 = this.parsePrice(product.precio_optimista_12m).value || 0;
    const bu24 = this.parsePrice(product.precio_optimista_24m).value || 0;

    badges.push(`<span class='tesis-badge' style='color:${{'excedida':'#8b5cf6','fuerte':'#16a34a','confirmada':'#3b82f6','debil':'#f59e0b','rota':'#dc2626','pending':'#6b7280'}[teClass]||'#6b7280'};background:${{'excedida':'#f5f3ff','fuerte':'#f0fdf4','confirmada':'#eff6ff','debil':'#fffbeb','rota':'#fef2f2','pending':'#f3f4f6'}[teClass]||'#f3f4f6'}'>${teLabel}</span>`);

    /* Upside/Downside */
    if (actual > 0 && b6 > 0 && br6 > 0 && bu6 > 0) {
      const up6 = (bu6 - b6) / b6, dn6 = (b6 - br6) / b6;
      const up12 = b12 > 0 ? (bu12 - b12) / b12 : 0, dn12 = b12 > 0 ? (b12 - br12) / b12 : 0;
      const up24 = b24 > 0 ? (bu24 - b24) / b24 : 0, dn24 = b24 > 0 ? (b24 - br24) / b24 : 0;
      const aUp = up6 * 3 + (up6 + up12) * 3 + (up12 + up24) * 6;
      const aDn = dn6 * 3 + (dn6 + dn12) * 3 + (dn12 + dn24) * 6;
      const ratio = aDn > 0 ? aUp / aDn : 0;

      let oLabel, oColor, oBg;
      if (ratio >= 1.5) { oLabel = 'Muy favorable'; oColor = '#15803d'; oBg = '#f0fdf4'; }
      else if (ratio >= 1.0) { oLabel = 'Favorable'; oColor = '#16a34a'; oBg = '#f0fdf4'; }
      else if (ratio >= 0.7) { oLabel = 'Equilibrado'; oColor = '#1e40af'; oBg = '#eff6ff'; }
      else { oLabel = 'Desfavorable'; oColor = '#dc2626'; oBg = '#fef2f2'; }
      badges.push(`<span class='tesis-badge' style='color:${oColor};background:${oBg}'>${oLabel}</span>`);
    }

    /* Asymmetry */
    if (actual > 0 && b6 > 0) {
      let realPrice = actual;
      const evo = product.price_evolution || [];
      if (evo.length > 0) {
        const lp = this.parsePrice(evo[evo.length - 1].price).value;
        if (lp > 0) realPrice = lp;
      }
      const vt = (product.validated_tesis || '').trim();
      let ref6m = b6;
      if (vt === 'Optimista' && bu6 > 0) ref6m = bu6;
      else if (vt === 'Pesimista' && br6 > 0) ref6m = br6;
      const gapPct = (realPrice - ref6m) / ref6m * 100;

      let aLabel, aColor, aBg;
      if (gapPct <= -30) { aLabel = 'Recorrido al alza fuerte'; aColor = '#15803d'; aBg = '#dcfce7'; }
      else if (gapPct <= -10) { aLabel = 'Recorrido al alza leve'; aColor = '#16a34a'; aBg = '#f0fdf4'; }
      else if (gapPct < 10) { aLabel = 'Neutra'; aColor = '#3b82f6'; aBg = '#eff6ff'; }
      else if (gapPct < 30) { aLabel = 'Recorrido a la baja leve'; aColor = '#f59e0b'; aBg = '#fffbeb'; }
      else { aLabel = 'Recorrido a la baja fuerte'; aColor = '#dc2626'; aBg = '#fef2f2'; }
      badges.push(`<span class='tesis-badge' style='color:${aColor};background:${aBg}'>${aLabel}</span>`);
    }

    if (badges.length === 0) return '';
    return `<div class='latest-reports__analysis-badges'>${badges.join('')}</div>`;
  }

  setupPremiumTooltips() {
    const tooltip = this.section.querySelector('.latest-reports__premium-tooltip');
    if (!tooltip) return;

    const lockedCharts = this.section.querySelectorAll('[data-premium-locked="true"]');
    if (lockedCharts.length === 0) return;

    lockedCharts.forEach(chart => {
      chart.addEventListener('mouseenter', () => {
        const rect = chart.getBoundingClientRect();
        tooltip.style.display = 'block';
        tooltip.style.position = 'fixed';
        tooltip.style.zIndex = '9999';
        const tipW = tooltip.offsetWidth;
        const left = rect.left + rect.width / 2 - tipW / 2;
        tooltip.style.left = Math.max(4, Math.min(left, window.innerWidth - tipW - 4)) + 'px';
        tooltip.style.top = (rect.top - tooltip.offsetHeight - 6) + 'px';
      });

      chart.addEventListener('mouseleave', () => {
        tooltip.style.display = 'none';
      });
    });
  }

  escapeHTML(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  showError(message) {
    this.grid.innerHTML = `
      <div class='latest-reports__error'>
        <p>${message}</p>
      </div>
    `;
  }

  showEmpty() {
    this.grid.innerHTML = `
      <div class='latest-reports__empty'>
        <h3>No hay informes disponibles</h3>
        <p>No se encontraron informes en esta colección.</p>
      </div>
    `;
  }

  setupCardObservers() {
    const options = {
      root: null,
      rootMargin: '0px 0px -50px 0px',
      threshold: 0.2
    };

    this.cards.forEach((card) => {
      const progressBars = card.querySelectorAll('.latest-reports__progress-fill');

      progressBars.forEach(bar => {
        bar.style.animationPlayState = 'paused';
      });

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.animateCardProgressBars(card, progressBars);
            observer.unobserve(entry.target);
          }
        });
      }, options);

      observer.observe(card);
      this.observers.push(observer);
    });
  }

  animateCardProgressBars(card, progressBars) {
    if (card.hasAttribute('data-animated')) return;

    card.setAttribute('data-animated', 'true');

    progressBars.forEach((bar, index) => {
      setTimeout(() => {
        bar.style.animationPlayState = 'running';
      }, index * 80);
    });
  }

  destroy() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    this.cards.forEach(card => card.removeAttribute('data-animated'));
  }
}

// Store instances
const latestReportsInstances = new Map();

// Initialize all sections
function initializeLatestReportsSections() {
  const sections = document.querySelectorAll('[id^="latest-reports-"]');

  sections.forEach(section => {
    const sectionId = section.id;
    if (sectionId && !latestReportsInstances.has(sectionId)) {
      const instance = new LatestReports(section);
      latestReportsInstances.set(sectionId, instance);
    }
  });
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', initializeLatestReportsSections);

// Fallback initialization
setTimeout(initializeLatestReportsSections, 100);

// Handle Shopify section reloads (theme editor)
document.addEventListener('shopify:section:load', function(event) {
  if (event.target.id?.startsWith('latest-reports-')) {
    const sectionId = event.target.id;

    if (latestReportsInstances.has(sectionId)) {
      latestReportsInstances.get(sectionId).destroy();
      latestReportsInstances.delete(sectionId);
    }

    const instance = new LatestReports(event.target);
    latestReportsInstances.set(sectionId, instance);
  }
});

// Handle section unload
document.addEventListener('shopify:section:unload', function(event) {
  if (event.target.id?.startsWith('latest-reports-')) {
    const sectionId = event.target.id;
    if (latestReportsInstances.has(sectionId)) {
      latestReportsInstances.get(sectionId).destroy();
      latestReportsInstances.delete(sectionId);
    }
  }
});
